import {
  ITeamResponseSchema,
  IUser,
  MCustomSelectProps,
} from '@monetize/types/app';
import { doGetTeamById } from '../../../../api/settingsService';
import { doGetTenantUser } from '../../../../api/usersService';
import { MBox } from '../../../../components/Monetize';
import { MCustomSelect } from '../../../../components/Monetize/MCustomSelect/MCustomSelect';
import { MCustomSelectTabItem } from '../../../../components/Monetize/MCustomSelect/MCustomSelectTabItem';
import { useCustomSelectValue } from '../../../../components/Monetize/MCustomSelect/useCustomSelectValue';

interface PersonTeamFilterProps extends MCustomSelectProps {
  tenantId: string;
  filterBy: {
    type: 'person' | 'team' | null;
  };
  setFilterBy: (filterBy: { type: 'person' | 'team' | null }) => void;
  filterValues: {
    person: string | null;
    team: string | null;
  };
  setFilterValues: (filterValues: {
    person: string | null;
    team: string | null;
  }) => void;
  clearable?: boolean;
}

const PersonTeamFilter = ({
  tenantId,
  filterBy,
  setFilterBy,
  filterValues,
  setFilterValues,
  clearable = false,
  ...rest
}: PersonTeamFilterProps) => {
  let props: any = {};
  if (filterBy.type === 'person') {
    props = {
      value: filterValues.person,
      onChange: (value: string) =>
        setFilterValues({ ...filterValues, person: value || '' }),

      endpoint: `/api/tenants/${tenantId}/users`,
      itemTitle: 'name',
      itemValue: 'id',
      doGetOneById: (userId: string) => doGetTenantUser(tenantId, userId),
    };
  } else {
    props = {
      value: filterValues.team,
      onChange: (value: string) =>
        setFilterValues({ ...filterValues, team: value || '' }),

      endpoint: `/api/teams`,
      itemTitle: 'name',
      itemValue: 'id',
      doGetOneById: doGetTeamById,
    };
  }

  const { doGetOneById, ...selectProps } = props;
  const {
    internalValue,
    isLoading: isLoadingSavedValue,
    onInternalValueChange,
  } = useCustomSelectValue<ITeamResponseSchema | IUser>({
    value: selectProps.value,
    setValue: selectProps.onChange,
    getOneById: doGetOneById,
  });

  const renderPreContent = () => (
    <MBox borderBottom={1} display="flex" mb={1} borderColor="tGray.darkPurple">
      <MCustomSelectTabItem
        active={filterBy.type === 'person'}
        onClick={() => setFilterBy({ type: 'person' })}
        text="Person"
      />
      <MCustomSelectTabItem
        active={filterBy.type === 'team'}
        onClick={() => setFilterBy({ type: 'team' })}
        text="Team"
      />
    </MBox>
  );

  return (
    <MCustomSelect
      placeholder="Select..."
      {...selectProps}
      renderPreContent={renderPreContent}
      showQueryInput
      loadAll={false}
      clearable={clearable}
      loading={isLoadingSavedValue}
      value={internalValue}
      returnItem
      onChange={onInternalValueChange}
      popOverContentProps={{ minWidth: 0 }}
      // Size to the input, not the header row: the select's default
      // `w="100%"` makes it a full-width flex item that squeezes the
      // "Show data for:" label onto two lines.
      w="auto"
      {...rest}
    />
  );
};

export { PersonTeamFilter };
